import React, { useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { supabase } from '../../../../components/supabase/config'; // Ensure this path is correct

// Converts "YYYY-MM-DDTHH:MM" -> "MM/DD/YYYY HH:MM"
function formatMMDDYYYY_HHMM(datetimeLocalValue) {
  if (!datetimeLocalValue) return '';

  const [datePart, timePart] = datetimeLocalValue.split('T');
  if (!datePart || !timePart) return '';

  const [yyyy, mm, dd] = datePart.split('-');
  const [HH, MM] = timePart.split(':');

  if (!yyyy || !mm || !dd || HH === undefined || MM === undefined) return '';

  return `${mm}/${dd}/${yyyy} ${HH}:${MM}`;
}

function CreateTrackingData() {
  // ADDED: allowed remarks list (dropdown options)
  const remarkOptions = [
    'Bangkok Warehouse',
    'Loaded',
    'Loading',
    'Sailing',
    'Clearance',
    'delivered',
    'arrived',
    'Manila Warehouse',
    'berthing',
    'out for delivery',
    'not found',
    'no tracking',
    'sailing near Phil.SEA',
    'Manila port',
    'Sorting of Boxes',
  ];

  const [formData, setFormData] = useState({
    id: '',
    sender: '',
    receiver: '',
    items: '',
    date_ordered: '', // "MM/DD/YYYY HH:MM"
    no_of_box: '',
    box_size: '',
    weight: '',
    address: '',
    destination: '',
    date_loaded: '',  // "MM/DD/YYYY HH:MM"
    remarks: '', // default blank
  });

  // Raw values for datetime-local inputs: "YYYY-MM-DDTHH:MM"
  const [dateOrderedRaw, setDateOrderedRaw] = useState('');
  const [dateLoadedRaw, setDateLoadedRaw] = useState('');

  const [notification, setNotification] = useState(null);

  // Handle Input Change (general fields)
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  // Date Ordered change (datetime-local -> formatted string)
  const handleDateOrderedChange = (e) => {
    const raw = e.target.value; // "YYYY-MM-DDTHH:MM"
    setDateOrderedRaw(raw);

    const formatted = formatMMDDYYYY_HHMM(raw); // "MM/DD/YYYY HH:MM"
    setFormData((prev) => ({
      ...prev,
      date_ordered: formatted,
    }));
  };

  // Date Loaded change (datetime-local -> formatted string)
  const handleDateLoadedChange = (e) => {
    const raw = e.target.value; // "YYYY-MM-DDTHH:MM"
    setDateLoadedRaw(raw);

    const formatted = formatMMDDYYYY_HHMM(raw); // "MM/DD/YYYY HH:MM"
    setFormData((prev) => ({
      ...prev,
      date_loaded: formatted,
    }));
  };

  // Handle Submit for form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setNotification(null);

    // ADDED: popup validation for blank remarks
    if (!formData.remarks) {
      window.alert('Please choose the remarks');
      return;
    }

    // 1) Fetch all remarks for the given tracking number
    const { data: existingEntries, error: fetchError } = await supabase
      .from('trackingfbt_audit')
      .select('remarks, tracking_id')
      .eq('tracking_id', formData.id);

    if (fetchError) {
      console.error('Error fetching existing remarks:', fetchError);
      setNotification({
        type: 'error',
        message: 'An error occurred while checking for duplicates.',
      });
      return;
    }

    // 2) Check if any existing remark matches the new one
    const isDuplicate = existingEntries.find(
      (entry) => entry.remarks === formData.remarks
    );

    if (isDuplicate) {
      setNotification({
        type: 'error',
        message: `Tracking number "${formData.id}" has a duplicate remark. Submission not successful.`,
      });
      return;
    }

    // 3) If no duplicate, insert the new tracking data
    const { error } = await supabase.from('trackingfbt').insert([formData]);

    if (error) {
      setNotification({ type: 'error', message: 'Failed to add data: ' + error.message });
    } else {
      setNotification({ type: 'success', message: 'Customer details added successfully!' });

      setFormData({
        id: '',
        sender: '',
        receiver: '',
        items: '',
        date_ordered: '',
        no_of_box: '',
        box_size: '',
        weight: '',
        address: '',
        destination: '',
        date_loaded: '',
        remarks: '', // reset to blank
      });

      setDateOrderedRaw('');
      setDateLoadedRaw('');
    }
  };

  return (
    <div className="container py-4">
      <h1 className="text-center mb-4">Add Entry Form</h1>

      {/* Notification messages */}
      {notification && (
        <div className={`alert ${notification.type === 'error' ? 'alert-danger' : 'alert-success'}`}>
          {notification.message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="text-dark bg-light p-4 rounded shadow-sm">
        <div className="mb-3">
          <label htmlFor="id" className="form-label">Tracking Number</label>
          <input
            type="text"
            id="id"
            name="id"
            value={formData.id}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="mb-3">
          <label htmlFor="sender" className="form-label">Sender</label>
          <input
            type="text"
            id="sender"
            name="sender"
            value={formData.sender}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="mb-3">
          <label htmlFor="receiver" className="form-label">Receiver</label>
          <input
            type="text"
            id="receiver"
            name="receiver"
            value={formData.receiver}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="mb-3">
          <label htmlFor="items" className="form-label">Items</label>
          <input
            type="text"
            id="items"
            name="items"
            value={formData.items}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        {/* CHANGED: Remarks input -> dropdown (default blank) */}
        <div className="mb-3">
          <label htmlFor="remarks" className="form-label">Remarks</label>
          <select
            id="remarks"
            name="remarks"
            value={formData.remarks}
            onChange={handleChange}
            className="form-select"
          >
            <option value="">-- Select remarks --</option>
            {remarkOptions.map((remark) => (
              <option key={remark} value={remark}>
                {remark}
              </option>
            ))}
          </select>
        </div>

        {/* Date Ordered as datetime */}
        <div className="mb-3">
          <label htmlFor="date_ordered" className="form-label">
            Date Ordered (MM/DD/YYYY HH:MM)
          </label>
          <input
            type="datetime-local"
            id="date_ordered"
            name="date_ordered"
            value={dateOrderedRaw}
            onChange={handleDateOrderedChange}
            className="form-control"
          />
          {formData.date_ordered && (
            <div className="form-text">
              Saved format: <strong>{formData.date_ordered}</strong>
            </div>
          )}
        </div>

        <div className="mb-3">
          <label htmlFor="no_of_box" className="form-label">Number of Boxes</label>
          <input
            type="number"
            id="no_of_box"
            name="no_of_box"
            value={formData.no_of_box}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="mb-3">
          <label htmlFor="box_size" className="form-label">Box Size</label>
          <input
            type="text"
            id="box_size"
            name="box_size"
            value={formData.box_size}
            onChange={handleChange}
            className="form-control"
          />
        </div>

        <div className="mb-3">
          <label htmlFor="weight" className="form-label">Weight</label>
          <input
            type="text"
            id="weight"
            name="weight"
            value={formData.weight}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="mb-3">
          <label htmlFor="address" className="form-label">Address</label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="mb-3">
          <label htmlFor="destination" className="form-label">Destination</label>
          <input
            type="text"
            id="destination"
            name="destination"
            value={formData.destination}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        {/* Date Loaded now matches Date Ordered format */}
        <div className="mb-3">
          <label htmlFor="date_loaded" className="form-label">
            Date Loaded (MM/DD/YYYY HH:MM)
          </label>
          <input
            type="datetime-local"
            id="date_loaded"
            name="date_loaded"
            value={dateLoadedRaw}
            onChange={handleDateLoadedChange}
            className="form-control"
          />
          {formData.date_loaded && (
            <div className="form-text">
              Saved format: <strong>{formData.date_loaded}</strong>
            </div>
          )}
        </div>

        <button type="submit" className="btn btn-primary">Add Data</button>
      </form>
    </div>
  );
}

export default CreateTrackingData;
